import { useMemo, useState } from 'react';
import { AdvancedFilterButton, FilterPanelCollapse } from '@/components/common/AdvancedFilter';
import { DataTable } from '@/components/common/DataTable';
import { DateRangeBadge } from '@/components/common/DateRangeBadge';
import { ErrorState } from '@/components/common/ErrorState';
import { StatCardGrid } from '@/components/common/StatCardGrid';
import { PageHeader } from '@/components/layout/PageHeader';
import { FIT_TO_SCREEN_PAGE_CLASS } from '@/components/layout/page.constants';
import { useInfiniteTableQuery } from '@/hooks/useInfiniteTableQuery';
import { useRevealLastViewedRow } from '@/hooks/useLastViewedRow';
import { specimenDistributionInfiniteQueryOptions } from '../api/product.api';
import { ProductFilterPanel } from '../components/ProductFilterPanel';
import { getSpecimenDistributionColumns } from '../components/ProductColumns';
import { APPROVE_STATUS, SPECIMEN_REPORT_SEARCH_PLACEHOLDER } from '../constants/product.constants';
import { useDistributionFilters } from '../hooks/useDistributionFilters';
import {
  getApproveStatusFilterField,
  getInstitutionNoFilterField,
  getPhoneFilterField,
} from '../utils/productFilterFields';
import { getSpecimenDistributionStatCards } from '../utils/productStatCards';

const PAGE_TITLE = 'Specimen Distribution';

const APPROVAL_TITLE_LABEL: Partial<Record<number, string>> = {
  [APPROVE_STATUS.APPROVED]: 'Approved',
  [APPROVE_STATUS.PENDING]: 'Pending',
};

const PAGE_FILTER_FIELDS = [
  getApproveStatusFilterField(),
  getPhoneFilterField('Teacher Phone No'),
  getInstitutionNoFilterField(),
];

function getPageTitle(approveTypeNo: number | undefined): string {
  const approvalLabel =
    approveTypeNo === undefined ? undefined : APPROVAL_TITLE_LABEL[approveTypeNo];
  return approvalLabel === undefined ? PAGE_TITLE : `${PAGE_TITLE} (${approvalLabel})`;
}

export function SpecimenDistributionPage() {
  const controller = useDistributionFilters('distribution');
  const { rows, firstPageMeta, isLoading, loadError, refetch, loadMoreProps } =
    useInfiniteTableQuery(specimenDistributionInfiniteQueryOptions(controller.params));

  const [isFilterOpen, setIsFilterOpen] = useState(false);
  const lastViewedSpecimenId = useRevealLastViewedRow('specimens');

  const columns = useMemo(
    () => getSpecimenDistributionColumns(controller.drillParams, controller.params.search),
    [controller.drillParams, controller.params.search],
  );

  return (
    <div className={FIT_TO_SCREEN_PAGE_CLASS}>
      <PageHeader
        title={getPageTitle(controller.params.approveTypeNo)}
        titleAddon={
          <DateRangeBadge
            onClick={() => setIsFilterOpen(true)}
            dateFrom={controller.params.dateFrom}
            dateTo={controller.params.dateTo}
          />
        }
        search={{
          value: controller.searchText,
          onChange: controller.setSearchText,
          placeholder: SPECIMEN_REPORT_SEARCH_PLACEHOLDER,
        }}
        actions={
          <AdvancedFilterButton
            isOpen={isFilterOpen}
            activeCount={controller.activeFilterCount}
            onClick={() => setIsFilterOpen((wasOpen) => !wasOpen)}
          />
        }
      />

      <FilterPanelCollapse isOpen={isFilterOpen}>
        <ProductFilterPanel filters={controller} pageFields={PAGE_FILTER_FIELDS} />
      </FilterPanelCollapse>

      {loadError ? (
        <ErrorState
          error={loadError}
          fallbackTitle="Could not load the distribution"
          onRetry={() => void refetch()}
          fill
        />
      ) : (
        <>
          <StatCardGrid
            isLoading={isLoading}
            stats={getSpecimenDistributionStatCards(firstPageMeta)}
          />

          <DataTable
            data={rows}
            columns={columns}
            isLoading={isLoading}
            className="lg:flex-1"
            stickyColumnId="name"
            isRowHighlighted={(specimen) => specimen.specimenId === lastViewedSpecimenId}
            emptyMessage="No specimen went out in this period"
            emptyHint="Try a wider date range or a different area."
            {...loadMoreProps}
          />
        </>
      )}
    </div>
  );
}
